import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {buildSettingsDevelopersPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum, SettingsDevelopersUrl} from '../../../models/url/settingsUrlModel';
import {SettingsBreadcrumbs} from '../../common/settingsBreadcrumbs';

/*
 * Props.
 */

interface SettingsDevelopersBreadcrumbsProps {
  url: SettingsDevelopersUrl;
}

/*
 * Component.
 */

export const SettingsDevelopersBreadcrumbs: FC<SettingsDevelopersBreadcrumbsProps> = (props) => {
  const {url} = props;
  const {navigateToLink} = useLinkNavigation();

  // If we are on the base page or on the tokens page and do not have a token selected
  // we should not render anything.
  if (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATIONS ||
    (url.subPage === SettingsDevelopersPagesEnum.TOKENS && !url.tokenId)
  ) {
    return (
      <SettingsBreadcrumbs
        topLevelBreadcrumb={{
          label: '',
          onBreadcrumbClick: () => {},
        }}
        breadcrumbs={[]}
      />
    );
  }

  // They are currently viewing a feature.
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES && url.feature) {
    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="developers-settings-title-breadcrumb-features">Features</Trans>,
          onBreadcrumbClick: () =>
            navigateToLink(
              buildSettingsDevelopersPath({
                rootPage: url.rootPage,
                applicationId: url.applicationId,
                subPage: url.subPage,
              }),
            ),
        }}
        breadcrumbs={[]}
      />
    );
  }

  // They are currently viewing a server.
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_SERVERS && (url.serverId || url.isConnecting)) {
    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="developers-settings-title-breadcrumb-servers">Servers</Trans>,
          onBreadcrumbClick: () =>
            navigateToLink(
              buildSettingsDevelopersPath({
                rootPage: url.rootPage,
                applicationId: url.applicationId,
                subPage: url.subPage,
              }),
            ),
        }}
        breadcrumbs={[]}
      />
    );
  }

  // They are viewing a token.
  if (url.subPage === SettingsDevelopersPagesEnum.TOKENS && url.tokenId) {
    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="developers-settings-title-breadcrumb-tokens">API Tokens</Trans>,
          onBreadcrumbClick: () =>
            navigateToLink(
              buildSettingsDevelopersPath({
                rootPage: url.rootPage,
                subPage: url.subPage,
              }),
            ),
        }}
        breadcrumbs={[]}
      />
    );
  }

  return (
    <SettingsBreadcrumbs
      showSeparatorAfterTopLevel
      topLevelBreadcrumb={{
        label: <Trans id="developers-settings-title-breadcrumb-all-apps">All apps</Trans>,
        onBreadcrumbClick: () =>
          navigateToLink(
            buildSettingsDevelopersPath({
              rootPage: url.rootPage,
              subPage: SettingsDevelopersPagesEnum.APPLICATIONS,
            }),
          ),
      }}
      breadcrumbs={[]}
    />
  );
};
